// Public API for the player overlay-window module.
import React, { createRef, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { OverlayWindow } from './OverlayWindow';
import type { OverlayWindowAPI } from './OverlayWindow';
import { computeContentBoxPosition } from '../player/utils/computeContentBoxPosition';
import type {
  OverlayWindowHandle,
  OverlayWindowOptions,
  OverlayWindowTarget,
} from './types';

/**
 * Resolve an OverlayWindowTarget (string id or HTMLElement) to an HTMLElement,
 * logging an error and returning null if a string id has no matching node.
 */
function resolveTarget(
  target: OverlayWindowTarget,
  context: string,
): HTMLElement | null {
  if (typeof target === 'string') {
    const el = document.getElementById(target);
    if (!el) {
      console.error(`${context}: target element not found:`, target);
      return null;
    }
    return el;
  }
  return target;
}

/**
 * Renders an existing DOM node as React content. Used to embed an HTMLElement
 * passed via the mount-function `content` option inside the window body.
 */
function DomNode({ node }: { node: HTMLElement }) {
  const hostRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    host.appendChild(node);
    return () => {
      if (node.parentElement === host) host.removeChild(node);
    };
  }, [node]);
  return React.createElement('div', { ref: hostRef, className: 'kollab-overlay-window-content' });
}

/** Build the React content for the window body from the `content` option. */
function renderContent(content: OverlayWindowOptions['content']): React.ReactNode {
  if (content == null) return null;
  if (typeof content === 'string') {
    return React.createElement('div', {
      className: 'kollab-overlay-window-content',
      dangerouslySetInnerHTML: { __html: content },
    });
  }
  return React.createElement(DomNode, { node: content });
}

window.mountPlayerOverlayWindow = function mountPlayerOverlayWindow(
  playerTargetOrId: OverlayWindowTarget,
  options: OverlayWindowOptions = {},
): OverlayWindowHandle {
  const target = resolveTarget(playerTargetOrId, 'OverlayWindow');
  if (!target) {
    return { unmount: () => {}, get api() { return null; } };
  }

  // Create the root and size it to the video content area (object-fit:contain),
  // mirroring the annotation editor's syncEditorRoot. The window is laid out and
  // bounded relative to this root.
  const root = document.createElement('div');
  root.setAttribute('data-overlay-window-root', '');
  root.className = 'kollab-overlay-window-root';
  target.appendChild(root);

  // What the root tracks, in priority order:
  //  1. An explicit `anchorSelector` element (e.g. audio's waveform) — sized to
  //     that element's box. Used by players with no <video>.
  //  2. The <video> content box (object-fit:contain area) — the default.
  //  3. Fallback: the full target rect, if neither is available.
  const anchorEl = options.anchorSelector
    ? (target.querySelector(options.anchorSelector) as HTMLElement | null)
    : null;
  const videoEl = target.querySelector('video') as HTMLVideoElement | null;

  // Position the root to an arbitrary anchor element, relative to the target.
  const syncRootToAnchor = (el: HTMLElement) => {
    const targetRect = target.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    root.style.width = elRect.width + 'px';
    root.style.height = elRect.height + 'px';
    root.style.top = elRect.top - targetRect.top + 'px';
    root.style.left = elRect.left - targetRect.left + 'px';
  };

  // Position the root to the <video> content box (object-fit:contain).
  const syncRootToVideo = () => {
    if (!videoEl || !videoEl.videoWidth) return;
    const targetRect = target.getBoundingClientRect();
    const videoRect = videoEl.getBoundingClientRect();
    const pos = computeContentBoxPosition({
      videoNativeWidth: videoEl.videoWidth,
      videoNativeHeight: videoEl.videoHeight,
      videoElementWidth: videoEl.clientWidth,
      videoElementHeight: videoEl.clientHeight,
      videoTopInWrapper: videoRect.top - targetRect.top,
      videoLeftInWrapper: videoRect.left - targetRect.left,
    });
    if (!pos) return;
    root.style.width = pos.width + 'px';
    root.style.height = pos.height + 'px';
    root.style.top = pos.top + 'px';
    root.style.left = pos.left + 'px';
  };

  // Size the root to the full target box (fallback when there's neither an
  // anchor nor a <video>, e.g. a viewer that fills its stage).
  const syncRootToTarget = () => {
    root.style.width = target.clientWidth + 'px';
    root.style.height = target.clientHeight + 'px';
    root.style.top = '0px';
    root.style.left = '0px';
  };

  let rootResizeObs: ResizeObserver | undefined;
  if (anchorEl) {
    const syncRoot = () => syncRootToAnchor(anchorEl);
    if (typeof ResizeObserver !== 'undefined') {
      rootResizeObs = new ResizeObserver(syncRoot);
      rootResizeObs.observe(anchorEl);
    }
    syncRoot();
  } else if (videoEl) {
    if (typeof ResizeObserver !== 'undefined') {
      rootResizeObs = new ResizeObserver(syncRootToVideo);
      rootResizeObs.observe(videoEl);
    }
    syncRootToVideo();
  } else if (typeof ResizeObserver !== 'undefined') {
    rootResizeObs = new ResizeObserver(syncRootToTarget);
    rootResizeObs.observe(target);
    syncRootToTarget();
  }

  const targetRef = { current: root } as React.RefObject<HTMLElement>;
  // Drag/resize bounds = the player's content area: the <video> element box
  // (which includes the letterbox/pillarbox black bars but not the controls
  // strip below it), or the anchor element (e.g. audio's waveform), falling
  // back to the whole target. This lets the window move off the object-fit
  // content box (the root) into the black bars, but never over the controls or
  // outside the player.
  const boundsEl = anchorEl ?? videoEl ?? target;
  const boundsRef = { current: boundsEl } as React.RefObject<HTMLElement>;
  const apiRef = createRef<OverlayWindowAPI>();
  const reactRoot = createRoot(root);

  const {
    backgroundOpacity,
    blur,
    initialPosition,
    initialSize,
    minSize,
    maxSize,
    scaleMode,
    alignXToReference,
    className,
    onClose,
    content,
  } = options;

  const handle: OverlayWindowHandle = {
    unmount: () => {
      rootResizeObs?.disconnect();
      reactRoot.unmount();
      root.remove();
    },
    get api() {
      return apiRef.current;
    },
  };

  flushSync(() => {
    reactRoot.render(
      React.createElement(
        OverlayWindow,
        {
          ref: apiRef,
          targetRef,
          boundsRef,
          backgroundOpacity,
          blur,
          initialPosition,
          initialSize,
          minSize,
          maxSize,
          scaleMode,
          alignXToReference,
          className,
          // Close button unmounts the window after firing the caller's onClose.
          onClose: () => {
            onClose?.();
            handle.unmount();
          },
        },
        renderContent(content),
      ),
    );
  });

  return handle;
};

// --- Re-exports for React usage ---

export { OverlayWindow } from './OverlayWindow';
export type { OverlayWindowProps, OverlayWindowAPI } from './OverlayWindow';
export type {
  OverlayWindowOptions,
  OverlayWindowHandle,
  OverlayWindowTarget,
  OverlayWindowScaleMode,
} from './types';
